// ═══════════════════════════════════════════════════════════
// CORNER DETECTION & VISUAL EFFECTS
// ═══════════════════════════════════════════════════════════

import { Corner, CornerType, Vector2, Bounds } from './types.js';
import { Utils } from './Utils.js';
import { Theme, applyNeonGlow, clearNeonGlow } from './Theme.js';

export class CornerManager {
    private corners: Corner[] = [];
    private hitRadius: number;
    private bounds: Bounds;
    private pulseTime: number = 0; // For pulsing animations

    constructor(bounds: Bounds, hitRadius: number) {
        this.bounds = bounds;
        this.hitRadius = hitRadius;
        this.initializeCorners();
    }

    private initializeCorners(): void {
        this.corners = [
            {
                position: { x: 0, y: 0 },
                type: CornerType.TOP_LEFT,
                glowIntensity: 0,
                hitCount: 0
            },
            {
                position: { x: this.bounds.width, y: 0 },
                type: CornerType.TOP_RIGHT,
                glowIntensity: 0,
                hitCount: 0
            },
            {
                position: { x: 0, y: this.bounds.height },
                type: CornerType.BOTTOM_LEFT,
                glowIntensity: 0,
                hitCount: 0
            },
            {
                position: { x: this.bounds.width, y: this.bounds.height },
                type: CornerType.BOTTOM_RIGHT,
                glowIntensity: 0,
                hitCount: 0
            }
        ];
    }

    update(deltaTime: number, ballPosition: Vector2): void {
        // Update pulse animation
        this.pulseTime += deltaTime * Theme.corners.pulseSpeed;

        for (const corner of this.corners) {
            const distance = Utils.distance(ballPosition, corner.position);

            // Update glow based on proximity
            if (distance < this.hitRadius * 2) {
                const proximity = 1 - (distance / (this.hitRadius * 2));
                corner.glowIntensity = Math.max(corner.glowIntensity, proximity);
            }

            // Decay glow (slower for more persistent effect)
            corner.glowIntensity *= 0.92;
        }
    }

    checkCornerHit(ballPosition: Vector2): Corner | null {
        for (const corner of this.corners) {
            const distance = Utils.distance(ballPosition, corner.position);

            if (distance < this.hitRadius) {
                corner.glowIntensity = 1.0;
                corner.hitCount++;
                return corner;
            }
        }

        return null;
    }

    isInAnyCorner(ballPosition: Vector2): boolean {
        for (const corner of this.corners) {
            const distance = Utils.distance(ballPosition, corner.position);
            if (distance < this.hitRadius) {
                return true;
            }
        }
        return false;
    }

    render(ctx: CanvasRenderingContext2D): void {
        // First render the neon race gates/portals
        for (const corner of this.corners) {
            this.renderNeonPortal(ctx, corner);
        }

        // Then render the threshold areas (checkpoint zones)
        this.renderThresholdAreas(ctx);
    }

    private renderThresholdAreas(ctx: CanvasRenderingContext2D): void {
        ctx.save();

        for (const corner of this.corners) {
            // Determine the quadrant for this corner
            const isLeft = corner.position.x === 0;
            const isTop = corner.position.y === 0;

            // Pulsing animation for threshold
            const pulseIntensity = 0.7 + Math.sin(this.pulseTime) * 0.3;

            // Create gradient for the threshold area (electric yellow)
            const gradient = ctx.createRadialGradient(
                corner.position.x,
                corner.position.y,
                0,
                corner.position.x,
                corner.position.y,
                this.hitRadius
            );

            // Parse yellow color from theme
            const yellowColor = Theme.corners.primaryColor;
            const r = parseInt(yellowColor.slice(1, 3), 16);
            const g = parseInt(yellowColor.slice(3, 5), 16);
            const b = parseInt(yellowColor.slice(5, 7), 16);

            // Yellow glow for checkpoint area
            gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${0.4 * pulseIntensity})`);
            gradient.addColorStop(0.5, `rgba(${r}, ${g}, ${b}, ${0.2 * pulseIntensity})`);
            gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);

            ctx.fillStyle = gradient;

            // Draw a quarter circle in the appropriate corner
            ctx.beginPath();
            if (isLeft && isTop) {
                // Top-left corner
                ctx.arc(corner.position.x, corner.position.y, this.hitRadius, 0, Math.PI / 2);
            } else if (!isLeft && isTop) {
                // Top-right corner
                ctx.arc(corner.position.x, corner.position.y, this.hitRadius, Math.PI / 2, Math.PI);
            } else if (isLeft && !isTop) {
                // Bottom-left corner
                ctx.arc(corner.position.x, corner.position.y, this.hitRadius, -Math.PI / 2, 0);
            } else {
                // Bottom-right corner
                ctx.arc(corner.position.x, corner.position.y, this.hitRadius, Math.PI, Math.PI * 1.5);
            }
            ctx.lineTo(corner.position.x, corner.position.y);
            ctx.closePath();
            ctx.fill();

            // Draw pulsing threshold circle outline
            applyNeonGlow(ctx, Theme.corners.primaryColor, 10);
            ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${0.4 * pulseIntensity})`;
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.arc(corner.position.x, corner.position.y, this.hitRadius, 0, Math.PI * 2);
            ctx.stroke();
            clearNeonGlow(ctx);
        }

        ctx.restore();
    }

    /**
     * Render neon race gate/portal at corner
     */
    private renderNeonPortal(ctx: CanvasRenderingContext2D, corner: Corner): void {
        ctx.save();

        const gateWidth = Theme.corners.gateWidth;
        const gateHeight = Theme.corners.gateHeight;

        // Determine corner direction for drawing
        const offsetX = corner.position.x === 0 ? 1 : -1;
        const offsetY = corner.position.y === 0 ? 1 : -1;

        // Calculate pulsing size
        const pulseScale = 1 + Math.sin(this.pulseTime) * 0.1;
        const currentGateWidth = gateWidth * pulseScale;
        const currentGateHeight = gateHeight * pulseScale;

        // Draw portal glow layers
        const glowIntensity = 0.4 + Math.sin(this.pulseTime * 1.5) * 0.2;

        for (let i = 0; i < 3; i++) {
            const glowRadius = (currentGateWidth + i * 20);
            const gradient = ctx.createRadialGradient(
                corner.position.x,
                corner.position.y,
                0,
                corner.position.x,
                corner.position.y,
                glowRadius
            );

            const yellowColor = Theme.corners.primaryColor;
            const r = parseInt(yellowColor.slice(1, 3), 16);
            const g = parseInt(yellowColor.slice(3, 5), 16);
            const b = parseInt(yellowColor.slice(5, 7), 16);

            const alpha = (glowIntensity - i * 0.1);
            gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${alpha})`);
            gradient.addColorStop(0.5, `rgba(${r}, ${g}, ${b}, ${alpha * 0.5})`);
            gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);

            ctx.fillStyle = gradient;
            ctx.fillRect(
                corner.position.x - glowRadius * (offsetX === 1 ? 0 : 1),
                corner.position.y - glowRadius * (offsetY === 1 ? 0 : 1),
                glowRadius,
                glowRadius
            );
        }

        // Draw gate frame (L-shaped neon lines)
        const lineWidth = 4;
        applyNeonGlow(ctx, Theme.corners.primaryColor, Theme.glow.cornerGlow);
        ctx.strokeStyle = Theme.corners.primaryColor;
        ctx.lineWidth = lineWidth;

        // Horizontal bar
        ctx.beginPath();
        ctx.moveTo(corner.position.x, corner.position.y);
        ctx.lineTo(corner.position.x + offsetX * currentGateWidth, corner.position.y);
        ctx.stroke();

        // Vertical bar
        ctx.beginPath();
        ctx.moveTo(corner.position.x, corner.position.y);
        ctx.lineTo(corner.position.x, corner.position.y + offsetY * currentGateHeight);
        ctx.stroke();

        clearNeonGlow(ctx);

        // Draw corner endpoint caps (glowing squares)
        const capSize = 8;
        applyNeonGlow(ctx, Theme.corners.secondaryColor, 15);
        ctx.fillStyle = Theme.corners.secondaryColor;

        // Corner point
        ctx.fillRect(
            corner.position.x - capSize / 2,
            corner.position.y - capSize / 2,
            capSize,
            capSize
        );

        // Horizontal endpoint
        ctx.fillRect(
            corner.position.x + offsetX * currentGateWidth - capSize / 2,
            corner.position.y - capSize / 2,
            capSize,
            capSize
        );

        // Vertical endpoint
        ctx.fillRect(
            corner.position.x - capSize / 2,
            corner.position.y + offsetY * currentGateHeight - capSize / 2,
            capSize,
            capSize
        );

        clearNeonGlow(ctx);

        // Draw "CHECK" text if active
        if (corner.glowIntensity > 0.3) {
            this.renderCheckpointText(ctx, corner, offsetX, offsetY, currentGateWidth, currentGateHeight);
        }

        // Draw pulsing center dot
        const dotPulse = 6 + Math.sin(this.pulseTime * 3) * 2;
        applyNeonGlow(ctx, Theme.colors.glowWhite, 15);
        ctx.fillStyle = Theme.colors.glowWhite;
        ctx.beginPath();
        ctx.arc(corner.position.x, corner.position.y, dotPulse, 0, Math.PI * 2);
        ctx.fill();
        clearNeonGlow(ctx);

        ctx.restore();
    }

    /**
     * Render "CHECK" text at checkpoint
     */
    private renderCheckpointText(
        ctx: CanvasRenderingContext2D,
        corner: Corner,
        offsetX: number,
        offsetY: number,
        gateWidth: number,
        gateHeight: number
    ): void {
        ctx.save();

        // Position text inside the gate
        const textX = corner.position.x + offsetX * (gateWidth * 0.5);
        const textY = corner.position.y + offsetY * (gateHeight * 0.5);

        // Text settings
        ctx.font = 'bold 14px "Courier New", monospace';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        // Pulsing effect
        const textAlpha = corner.glowIntensity * (0.8 + Math.sin(this.pulseTime * 4) * 0.2);

        // Draw text glow
        applyNeonGlow(ctx, Theme.corners.textGlow, 20);
        ctx.fillStyle = `rgba(255, 255, 255, ${textAlpha})`;
        ctx.fillText(Theme.corners.checkpointText, textX, textY);

        clearNeonGlow(ctx);

        ctx.restore();
    }

    reset(): void {
        for (const corner of this.corners) {
            corner.glowIntensity = 0;
            corner.hitCount = 0;
        }
    }

    updateBounds(bounds: Bounds): void {
        this.bounds = bounds;
        this.initializeCorners();
    }

    updateHitRadius(newRadius: number): void {
        this.hitRadius = newRadius;
    }

    getHitRadius(): number {
        return this.hitRadius;
    }
}
